import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getDeepObjectValue } from '../helpers/object';
import { isMobile } from 'react-device-detect';

import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import TPDivider from '../TPDivider';
import List from '@material-ui/core/List';

import TPTypography from '../TPTypography';
import TPCopyButton from '../TPCopyButton';

import useStyles from './styles';

const TPDescriptionList = ({
  value,
  descriptionProps,
  className,
  showEmpty = true,
  columns,
  size = 'medium',
  variant = 'normal',
  gridFr = isMobile ? '1' : '2',
}) => {
  const classes = useStyles();

  return value ? (
    variant === 'normal' ? (
      <dl
        className={classnames(classes.descriptionList, className, {
          [classes['columns' + columns]]: columns,
          [classes.smallSize]: size === 'small',
        })}>
        {descriptionProps
          ? descriptionProps.map((prop, index) => {
              let propValue = prop.dataKey ? getDeepObjectValue(value, prop.dataKey) : null;
              if (propValue && prop.format) {
                propValue = prop.format(propValue);
              }
              let renderValue = prop.renderItem && prop.renderItem(value, propValue);
              if (!propValue && !showEmpty && (!prop.renderItem || renderValue === null)) {
                return null;
              }
              let text = renderValue || propValue || '\u2014';
              const copy =
                prop.copyToClipboard !== undefined
                  ? typeof prop.copyToClipboard === 'function'
                    ? prop.copyToClipboard(value)
                    : prop.copyToClipboard
                  : false;

              const label = prop.getLabel?.(value) || prop.label;

              return (
                <div
                  className={classnames({
                    [classes.fullWidth]: prop.fullWidth,
                  })}
                  key={prop.dataKey + index}>
                  <TPTypography component="dt" variant="body3" color="secondary">
                    {label}
                  </TPTypography>
                  <TPTypography
                    component="dd"
                    variant={size === 'small' ? 'body2' : 'body1'}
                    className={classnames({
                      [classes[`${prop.textStyle}Text`]]: prop.textStyle,
                      [classes.wordBreakAll]: prop.wordBreakAll,
                    })}>
                    <>
                      <TPTypography component="span" variant="inherit">
                        {text}
                      </TPTypography>
                      {copy ? <TPCopyButton size="xsmall" text={text} /> : null}
                    </>
                  </TPTypography>
                </div>
              );
            })
          : null}
      </dl>
    ) : (
      <List className={classnames(className, classes.list)}>
        {descriptionProps.map((prop, index) => {
          let propValue = prop.dataKey ? getDeepObjectValue(value, prop.dataKey) : null;
          if (propValue && prop.format) {
            propValue = prop.format(propValue, value);
          }
          let renderValue = prop.renderItem && prop.renderItem(value, propValue);
          if (!propValue && !showEmpty && (!prop.renderItem || renderValue === null)) {
            return null;
          }
          let text = renderValue || propValue || '\u2014';

          const label = prop.getLabel?.(value) || prop.label;

          return (
            <div key={prop.dataKey + index}>
              <ListItem
                className={classnames(classes.listItem, className, {
                  [classes.summary]: prop.summary,
                })}
                style={{ gridTemplateColumns: `1fr ${gridFr}fr` }}>
                <ListItemText disableTypography>
                  <TPTypography variant="body1" color="secondary">
                    {label}
                  </TPTypography>
                </ListItemText>
                <ListItemText disableTypography>
                  <TPTypography variant={prop.summary ? 'h5' : 'body1'} bold>
                    {text}
                  </TPTypography>
                </ListItemText>
              </ListItem>
              {index !== descriptionProps.length - 1 && !descriptionProps[index + 1].summary ? (
                <TPDivider className={classes.divider} />
              ) : null}
            </div>
          );
        })}
      </List>
    )
  ) : null;
};

TPDescriptionList.displayName = 'MuiDescriptionList';

TPDescriptionList.propTypes = {
  /**
   * Data
   */
  value: PropTypes.object,
  /**
   * This value is responsible for showing or hiding the value
   */
  showEmpty: PropTypes.bool,
  size: PropTypes.oneOf(['medium', 'small']),
  /**
   * This value is responsible for showing data in columns, supported 1, 2, 3, 4 columns
   */
  columns: PropTypes.oneOf([1, 2, 3, 4]),
  /**
   * Config for every property, that should be displayed
   */
  descriptionProps: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Name(key) of value property, ex. email
       */
      dataKey: PropTypes.string.isRequired,
      /**
       * Label(title), e.g. Username
       */
      label: PropTypes.node.isRequired,
      /**
       * Function to get label(title) for property, e.g. (prop) => 'Username'
       */
      getLabel: PropTypes.func,
      /**
       * Text style of property value
       */
      textStyle: PropTypes.oneOf(['bold', '']),
      /**
       * Stretch prop item to container
       */
      fullWidth: PropTypes.bool,
      /**
       * Called when property value is rendered, function (value) => 'formatted string'
       */
      format: PropTypes.func,
      /**
       * Called when property value is rendered, function (item, value) => <Custom component />
       */
      renderItem: PropTypes.func,
      /**
       * Add copy icon button with ability to copy the item value to clipboard
       */
      copyToClipboard: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      /**
       * Apply 'word-break: break-all' style to the item, ensuring that the text will
       * wrap to the next line at any character to prevent overflow.
       */
      wordBreakAll: PropTypes.bool,
      /**
       * Special styles list item marker. Only for "rows" variant
       */
      summary: PropTypes.bool,
    }),
  ).isRequired,
  /**
   * Columns size in "fr" units (0-1). Only for "rows" variant
   */
  gridFr: PropTypes.string,
  variant: PropTypes.oneOf(['normal', 'rows']),
};

export default TPDescriptionList;
